import React, { useState } from 'react';
import { FiLayers as ExpandIcon, FiChevronRight as ChevronRight } from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

const Gallery = ({
  gallery = {},
  onOpenImage = () => {},
  apiBaseURL = '',
  maxItems = 8
}) => {
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();
  const [isHovered, setIsHovered] = useState(false);
  
  const items = Array.isArray(gallery.items) ? gallery.items : 
                (Array.isArray(gallery.images) ? gallery.images : []);

  const layout = gallery.layout || 'grid';
  const columns = gallery.columns || 3;
  const sectionTitle = gallery.sectionTitle || 'Our Campus';
  const sectionSubtitle = gallery.sectionSubtitle || 'A glimpse into the vibrant life at our institution';

  const isCarousel = layout === 'carousel';

  if (items.length === 0) return null;

  const visibleItems = getVisibleItems();
  function getVisibleItems() {
    return items.slice(0, maxItems);
  }

  // For a seamless CSS loop, we only need to duplicate the list ONCE ([...items, ...items])
  // Moving the container by exactly -50% creates a perfect, infinite loop.
  const duplicatedItems = [...items, ...items];

  const getGridColumns = () => {
    const colMap = {
      1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3',
      4: 'grid-cols-4', 5: 'grid-cols-5', 6: 'grid-cols-6'
    };
    return colMap[columns] || 'grid-cols-3';
  };

  return (
    <section id="gallery" className="py-20 md:py-28 bg-white overflow-hidden">
      {/* Dynamic Keyframe Injection for the Marquee */}
      {isCarousel && (
        <style dangerouslySetInnerHTML={{__html: `
          @keyframes marquee {
            0% { transform: translate3d(0, 0, 0); }
            100% { transform: translate3d(-50%, 0, 0); }
          }
          .marquee-track {
            display: flex;
            gap: 16px;
            width: max-content;
            animation: marquee ${items.length * 4}s linear infinite;
          }
          .marquee-paused {
            animation-play-state: paused;
          }
        `}} />
      )}

      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="inline-block text-xs font-light tracking-[0.2em] uppercase mb-3" style={{ color: secondary }}>
                Campus Life
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3" style={{ color: primary, fontFamily: fontHeading }}>
                {sectionTitle}
              </h2>
              <p className="text-lg font-light text-left md:max-w-lg" style={{ color: '#6B7280', fontFamily: fontBody }}>
                {sectionSubtitle}
              </p>
              <div className="mt-4 flex justify-start">
                <div className="h-1 w-20 rounded-full" style={{ background: `linear-gradient(90deg, ${primary}, ${secondary})` }} />
              </div>
            </div>
            {items.length > maxItems && !isCarousel && (
              <button
                onClick={() => onOpenImage(maxItems)}
                className="mt-4 md:mt-0 transition-colors flex items-center gap-2 group"
                style={{ color: '#6B7280', fontFamily: fontBody }}
              >
                <span>View all</span>
                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" style={{ color: secondary }} />
              </button>
            )}
          </div>

          {/* Gallery Layout */}
          {isCarousel ? (
            <div 
              className="relative overflow-hidden rounded-2xl bg-white py-2"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {/* This track runs perfectly on the GPU via CSS */}
              <div className={`marquee-track ${isHovered ? 'marquee-paused' : ''}`}>
                {duplicatedItems.map((image, index) => {
                  const imageUrl = image.url || image.image || '';
                  const imgObj = buildResolvedFile(imageUrl, apiBaseURL);
                  const src = imgObj.resolvedSrc || imageUrl;
                  const caption = image.caption || image.altText || '';
                  const originalIndex = index % items.length;

                  return (
                    <div
                      key={`${image._id || image.id || index}-${index}`}
                      className="relative flex-shrink-0 w-80 aspect-[4/3] cursor-pointer overflow-hidden rounded-xl bg-gray-100 transition-transform duration-300 hover:scale-105 hover:z-10 shadow-md"
                      onClick={() => onOpenImage(originalIndex)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onOpenImage(originalIndex);
                      }}
                    >
                      {src ? (
                        <img
                          src={src}
                          alt={caption || `gallery-${originalIndex}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ color: '#9CA3AF' }}>
                          <div className="text-center">
                            <ExpandIcon className="w-12 h-12 mx-auto mb-2" />
                            <span className="text-sm">No image</span>
                          </div>
                        </div>
                      )}

                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-4">
                        {caption && (
                          <p className="text-white text-sm font-medium line-clamp-2">
                            {caption}
                          </p>
                        )}
                        <div className="flex items-center gap-2 text-white/60 text-xs mt-1">
                          <ExpandIcon className="w-3 h-3" />
                          <span>Click to view</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Smooth side fading gradients */}
              <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white to-transparent pointer-events-none z-10" />
              <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white to-transparent pointer-events-none z-10" />
            </div>
          ) : (
            /* Grid Layout */
            <div className={`grid gap-3 md:gap-4 ${getGridColumns()} auto-rows-[180px] md:auto-rows-[220px]`}>
              {visibleItems.map((image, index) => {
                const imageUrl = image.url || image.image || '';
                const imgObj = buildResolvedFile(imageUrl, apiBaseURL);
                const src = imgObj.resolvedSrc || imageUrl;
                const caption = image.caption || image.altText || '';

                return (
                  <div
                    key={image._id || image.id || index}
                    className="group relative overflow-hidden rounded-xl cursor-pointer bg-gray-100 transition-all duration-500 hover:shadow-xl hover:z-10"
                    onClick={() => onOpenImage(index)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onOpenImage(index);
                    }}
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={caption || `gallery-${index}`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ color: '#9CA3AF' }}>
                        <div className="text-center">
                          <ExpandIcon className="w-8 h-8 mx-auto mb-1" />
                          <span className="text-xs">No image</span>
                        </div>
                      </div>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3">
                      {caption && (
                        <p className="text-white text-xs font-medium line-clamp-2">
                          {caption}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-white/50 text-[0.5rem] mt-0.5">
                        <ExpandIcon className="w-2.5 h-2.5" />
                        <span>View</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* View All Button for Carousel */}
          {isCarousel && items.length > 1 && (
            <div className="mt-8 text-center">
              <button
                onClick={() => onOpenImage(0)}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 hover:scale-105 active:scale-95"
                style={{ 
                  backgroundColor: `${primary}10`,
                  color: primary,
                  fontFamily: fontBody,
                }}
              >
                <span>View All Gallery</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Gallery;